import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useForm } from 'react-hook-form';
import { useSnackbar } from 'notistack';
import { makeStyles } from '@material-ui/core/styles';
import {
  Box,
  Button,
  CircularProgress,
  Grid,
  Paper,
  TextField,
  Typography,
} from '@material-ui/core';
import {
  getReferralContent,
  logReferralTabView,
  submitReferral,
} from '../../../actions/referral.actions';

const HOW_IT_WORKS_STEPS = [
  'Fill out the referral form',
  'Your colleagues sign a contract',
  'We send you a gift card',
];

const useStyles = makeStyles((theme) => ({
  stepNumber: {
    fontSize: 32,
    fontWeight: 700,
    lineHeight: 1,
    minWidth: 36,
  },
  stepBody: {
    fontSize: 18,
    lineHeight: 1.5,
  },
  stepRow: {
    display: 'flex',
    alignItems: 'flex-start',
    marginBottom: 24,
  },
  fieldRow: {
    marginBottom: theme.spacing(1),
  },
  fieldLabel: {
    fontSize: theme.typography.body2.fontSize,
    fontWeight: theme.typography.body2.fontWeight,
    lineHeight: '24px',
    color: theme.palette.text.primary,
    display: 'block',
    overflow: 'visible',
    paddingTop: 10,
  },
  fieldInput: {
    marginTop: 0,
    marginBottom: 0,
  },
  requiredMark: {
    color: theme.palette.error.main,
  },
}));

const ReferralProgram = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const { register, handleSubmit, reset, errors } = useForm();

  const [content, setContent] = useState('');
  const [giftCardAmount, setGiftCardAmount] = useState('$150');
  const [isLoadingContent, setIsLoadingContent] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});

  useEffect(() => {
    dispatch(logReferralTabView());
  }, [dispatch]);

  useEffect(() => {
    dispatch(getReferralContent())
      .then((data) => {
        setContent(data.content || '');
        setGiftCardAmount(data.gift_card_amount || '$150');
      })
      .catch(() => {
        enqueueSnackbar('Unable to load referral program content.', {
          variant: 'error',
        });
      })
      .finally(() => {
        setIsLoadingContent(false);
      });
  }, [dispatch, enqueueSnackbar]);

  const onSubmit = useCallback(
    (formData) => {
      setFieldErrors({});
      setIsSubmitting(true);
      dispatch(submitReferral(formData))
        .then((data) => {
          enqueueSnackbar('Referral submitted successfully.', {
            variant: 'success',
          });
          if (data?.notification_warning) {
            enqueueSnackbar(data.notification_warning, { variant: 'warning' });
          }
          if (data?.confirmation_warning) {
            enqueueSnackbar(data.confirmation_warning, { variant: 'warning' });
          }
          reset({
            property_name: '',
            contact_full_name: '',
            contact_email: '',
            contact_phone: '',
            contact_role: '',
            notes: '',
          });
        })
        .catch(({ response }) => {
          const { data } = response || {};
          setFieldErrors(data || {});
          enqueueSnackbar(
            'Error submitting referral. Please check the form and try again.',
            { variant: 'error' }
          );
        })
        .finally(() => {
          setIsSubmitting(false);
        });
    },
    [dispatch, enqueueSnackbar, reset]
  );

  const getHelperText = (name) => {
    if (errors[name]?.message) {
      return errors[name].message;
    }
    if (fieldErrors[name]) {
      return Array.isArray(fieldErrors[name])
        ? fieldErrors[name][0]
        : fieldErrors[name];
    }
    return null;
  };

  const renderField = (name, label, required = false, multiline = false) => (
    <Grid item xs={12} className={classes.fieldRow} key={name}>
      <Grid container alignItems="flex-start">
        <Grid item xs={5}>
          <Box className={classes.fieldLabel}>
            {label}
            {required && (
              <Box component="span" className={classes.requiredMark}>
                {' '}
                *
              </Box>
            )}
          </Box>
        </Grid>
        <Grid item xs={6}>
          <TextField
            id={`referral_${name}`}
            className={classes.fieldInput}
            margin="none"
            name={name}
            type="text"
            variant="outlined"
            placeholder={label}
            multiline={multiline}
            rows={multiline ? 4 : 1}
            size="small"
            inputRef={register({
              required: required ? `${label} is required.` : false,
              ...(name === 'contact_email' && {
                pattern: {
                  value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                  message: 'Email address is not in the right format.',
                },
              }),
            })}
            error={!!errors[name] || !!fieldErrors[name]}
            helperText={getHelperText(name)}
            fullWidth
          />
        </Grid>
      </Grid>
    </Grid>
  );

  return (
    <Box mr={4} pb={4}>
      <Paper elevation={0} variant="outlined">
        <Box py={4} pl={10} pr={3}>
          <Box mb={4}>
            <Typography variant="h6">Referral Program</Typography>
          </Box>
          {isLoadingContent ? (
            <Box display="flex" justifyContent="center" py={2}>
              <CircularProgress size={28} />
            </Box>
          ) : (
            <Typography variant="body2" style={{ whiteSpace: 'pre-wrap' }}>
              {content}
            </Typography>
          )}
        </Box>
      </Paper>

      <Box mt={4}>
        <Grid container spacing={4}>
          <Grid item xs={6}>
            <Paper elevation={0} variant="outlined">
              <Box py={4} pl={10} pr={3}>
                <Box mb={4}>
                  <Typography variant="h6">Referral Form</Typography>
                </Box>
                <form onSubmit={handleSubmit(onSubmit)}>
                  <Grid container>
                    {renderField('property_name', 'Property Name', true)}
                    {renderField(
                      'contact_full_name',
                      'Contact Person Full Name',
                      true
                    )}
                    {renderField('contact_email', 'Contact Email', true)}
                    {renderField('contact_phone', 'Contact Phone', true)}
                    {renderField('contact_role', 'Contact Role', true)}
                    {renderField('notes', 'Notes', false, true)}
                  </Grid>
                  <Box display="flex" justifyContent="flex-end" mt={4}>
                    <Button
                      id="referral_submit_button"
                      type="submit"
                      color="primary"
                      variant="contained"
                      size="large"
                      disabled={isSubmitting}
                    >
                      Submit Form
                    </Button>
                  </Box>
                </form>
              </Box>
            </Paper>
          </Grid>

          <Grid item xs={6}>
            <Paper elevation={0} variant="outlined" style={{ height: '100%' }}>
              <Box py={4} pl={10} pr={3}>
                <Box mb={4}>
                  <Typography variant="h6" align="center">
                    How It Works
                  </Typography>
                </Box>
                {HOW_IT_WORKS_STEPS.map((step, index) => (
                  <div key={step} className={classes.stepRow}>
                    <Typography className={classes.stepNumber}>
                      {index + 1}
                    </Typography>
                    <Box pt={0.5} pl={2}>
                      <Typography className={classes.stepBody}>
                        {index === 2
                          ? `We send you a ${giftCardAmount} gift card`
                          : step}
                      </Typography>
                    </Box>
                  </div>
                ))}
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default ReferralProgram;
